/**
 * Medivigil - Input Parser
 * Robust medication input parsing and preprocessing
 * 
 * DETERMINISTIC: Same input always produces same output
 * NO AI/LLM: Pure text processing
 * 
 * Handles:
 * - Multi-line paste (newline separated)
 * - Comma-separated lists
 * - Semicolon-separated lists
 * - Mixed delimiters
 * - Case normalization (preserves original for display)
 * - Whitespace trimming
 * - Empty line removal
 * - Deduplication
 */

/**
 * Parses a raw medication input string into an array of individual medications
 * @param {string} input - Raw input (may be multi-line, comma-separated, etc.)
 * @returns {object} - { medications: string[], originalInputs: string[], parseInfo: object }
 */
export function parseMedicationInput(input) {
  if (!input || typeof input !== 'string') {
    return {
      medications: [],
      originalInputs: [],
      parseInfo: {
        rawInput: input,
        totalParsed: 0,
        duplicatesRemoved: 0,
        emptyLinesRemoved: 0,
        delimiterUsed: 'none'
      }
    };
  }

  const rawInput = input;
  let entries = [];
  let delimiterUsed = 'none';

  // Step 1: Determine primary delimiter and split
  // Priority: newlines > semicolons > commas
  if (input.includes('\n')) {
    // Multi-line input - split by newlines first
    entries = input.split(/\r?\n/);
    delimiterUsed = 'newline';
    
    // Further split each line by commas and semicolons
    entries = entries.flatMap(line => splitBySecondaryDelimiters(line));
  } else if (input.includes(';')) {
    // Semicolon-separated
    entries = input.split(';');
    delimiterUsed = 'semicolon';
  } else if (input.includes(',')) {
    // Comma-separated
    entries = input.split(',');
    delimiterUsed = 'comma';
  } else {
    // Single entry
    entries = [input];
    delimiterUsed = 'single';
  }

  // Step 2: Clean each entry
  const cleanedEntries = entries.map(entry => cleanMedicationEntry(entry));

  // Step 3: Remove empty entries
  const emptyCount = cleanedEntries.filter(e => e.cleaned === '').length;
  const nonEmptyEntries = cleanedEntries.filter(e => e.cleaned !== '');

  // Step 4: Deduplicate (case-insensitive)
  const { unique, duplicatesRemoved } = deduplicateEntries(nonEmptyEntries);

  return {
    medications: unique.map(e => e.cleaned),
    originalInputs: unique.map(e => e.original),
    parseInfo: {
      rawInput,
      totalParsed: unique.length,
      duplicatesRemoved,
      emptyLinesRemoved: emptyCount,
      delimiterUsed
    }
  };
}

/**
 * Splits a line by secondary delimiters (comma, semicolon)
 * Used after initial newline split
 */
function splitBySecondaryDelimiters(line) {
  if (!line) return [];
  
  // Split by both comma and semicolon
  return line.split(/[,;]/).map(s => s.trim()).filter(s => s);
}

/**
 * Cleans a single medication entry
 * - Trims whitespace
 * - Preserves original for display
 * - Does NOT modify drug name beyond whitespace
 */
function cleanMedicationEntry(entry) {
  if (!entry || typeof entry !== 'string') {
    return { original: '', cleaned: '' };
  }

  const original = entry.trim();
  
  // Remove only leading/trailing whitespace
  // Preserve internal spacing (e.g., "Co codamol" should stay as is)
  const cleaned = original;

  return { original, cleaned };
}

/**
 * Deduplicates entries using case-insensitive comparison
 * Keeps the first occurrence's original casing
 */
function deduplicateEntries(entries) {
  const seen = new Set();
  const unique = [];
  let duplicatesRemoved = 0;

  for (const entry of entries) {
    const normalizedKey = entry.cleaned.toLowerCase().trim();
    
    if (!seen.has(normalizedKey)) {
      seen.add(normalizedKey);
      unique.push(entry);
    } else {
      duplicatesRemoved++;
    }
  }

  return { unique, duplicatesRemoved };
}

/**
 * Adds multiple medications from a parsed input to an existing array
 * @param {string[]} existingMedications - Current medication list
 * @param {string} newInput - Raw input to parse and add
 * @returns {object} - { medications: string[], added: number, duplicates: number }
 */
export function addMedicationsFromInput(existingMedications, newInput) {
  const parsed = parseMedicationInput(newInput);
  
  if (parsed.medications.length === 0) {
    return {
      medications: existingMedications,
      added: 0,
      duplicates: 0,
      parseInfo: parsed.parseInfo
    };
  }

  // Check against existing medications (case-insensitive)
  const existingLower = new Set(existingMedications.map(m => m.toLowerCase()));
  const toAdd = [];
  let duplicatesWithExisting = 0;

  for (let i = 0; i < parsed.medications.length; i++) {
    const med = parsed.medications[i];
    const original = parsed.originalInputs[i];
    
    if (!existingLower.has(med.toLowerCase())) {
      toAdd.push(original); // Use original casing for display
      existingLower.add(med.toLowerCase());
    } else {
      duplicatesWithExisting++;
    }
  }

  return {
    medications: [...existingMedications, ...toAdd],
    added: toAdd.length,
    duplicates: parsed.parseInfo.duplicatesRemoved + duplicatesWithExisting,
    parseInfo: parsed.parseInfo
  };
}

/**
 * Validates that a medication list is not empty
 * @param {string[]} medications - Array of medications
 * @returns {object} - { valid: boolean, message: string }
 */
export function validateMedicationList(medications) {
  if (!medications || !Array.isArray(medications)) {
    return {
      valid: false,
      message: 'No medications detected. Please enter at least one medication.'
    };
  }

  if (medications.length === 0) {
    return {
      valid: false,
      message: 'No medications detected. Please enter at least one medication.'
    };
  }

  return {
    valid: true,
    message: `${medications.length} medication(s) entered.`
  };
}

/**
 * Preprocesses medication input for paste events
 * Detects if input contains multiple medications
 * @param {string} input - Pasted text
 * @returns {object} - { isMultiple: boolean, medications: string[] }
 */
export function detectMultipleMedications(input) {
  if (!input) {
    return { isMultiple: false, medications: [] };
  }

  const hasNewlines = /\r?\n/.test(input);
  const hasCommas = input.includes(',');
  const hasSemicolons = input.includes(';');

  if (hasNewlines || hasCommas || hasSemicolons) {
    const parsed = parseMedicationInput(input);
    return {
      isMultiple: parsed.medications.length > 1,
      medications: parsed.originalInputs
    };
  }

  return {
    isMultiple: false,
    medications: [input.trim()].filter(m => m)
  };
}

/**
 * Extracts medications from unstructured text (e.g., doctor's notes)
 * @param {string} text - The unstructured text
 * @param {string[]} medNames - Array of known medication names
 * @returns {string[]} - Array of extracted medication names
 */
export function extractMedicationsFromText(text, medNames) {
  if (!text) return { medications: [], reportOnly: [] };

  const reportOnly = [];

  const sortedNames = medNames.slice().sort((a, b) => b.length - a.length);
  const escaped = sortedNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const exactRegex = new RegExp('\\b(' + escaped.join('|') + ')\\b', 'gi');

  const dosageRegex = /\b(\d+[\.,oO0-9]*\s*(?:mg|mcg|g|iu|units|ml|meq|tab|tabs|cap|caps|puffs|drops?|x\s*per|injection)|po|qd|bid|tid|qid|qam|qhs|q\d{1,2}h|prn|od|daily|weekly|monthly|bedtime)\b/gi;

  const stopWords = new Set([
    'take', 'give', 'gave', 'taking', 'taken', 'increase', 'decrease',
    'increased', 'decreased', 'changed', 'changing', 'switched', 'switching', 'switch',
    'reduce', 'reduced', 'taper', 'tapering',
    'start', 'started', 'stop', 'stopped', 'continue', 'continued',
    'patient', 'pt', 'with', 'without', 'and', 'for', 'the', 'this',
    'that', 'her', 'his', 'their', 'from', 'into', 'onto', 'about',
    'to', 'at', 'by', 'on', 'in', 'is', 'are', 'was', 'were',
    'be', 'been', 'has', 'have', 'had', 'do', 'does', 'did', 'not', 'no', 'yes', 'or', 'as', 'if',
    'blood', 'pressure', 'sugar', 'glucose', 'weight', 'height',
    'total', 'target', 'max', 'min', 'level', 'levels', 'value',
    'dose', 'doses', 'dosage', 'tablet', 'tablets', 'capsule', 'capsules',
    'fluid', 'fluids', 'restriction', 'intake', 'output',
    'hfa', 'cfa', 'er', 'xr', 'xl', 'sr', 'cr', 'dr', 'hcl', 'sulfate',
    'maleate', 'fumarate', 'succinate', 'tartrate', 'bromide', 'chloride', 'mesylate', 'besylate', 'besilate', 'acetate', 'phosphate',
    'ointment', 'cream', 'patch', 'inhaler', 'nebules', 'suspension', 'solution', 'syrup', 'elixir', 'lotion', 'gel', 'suppository',
    'before', 'after', 'during', 'between', 'meals', 'meal', 'food',
    'morning', 'evening', 'night', 'today', 'tomorrow', 'yesterday',
    'each', 'every', 'one', 'two', 'three', 'four', 'five', 'six',
    'mcg', 'units', 'unit', 'meq', 'tab', 'tabs', 'cap', 'caps',
    'puff', 'puffs', 'drop', 'drops', 'mls',
    'oral', 'orally', 'inhalation', 'inhaled', 'inhale', 'inh',
    'nebulized', 'neb', 'topical', 'topically', 'sublingual',
    'subcutaneous', 'subq', 'intramuscular', 'intravenous',
    'transdermal', 'rectal', 'rectally', 'vaginal', 'ophthalmic',
    'otic', 'nasal', 'buccal',
    'daily', 'weekly', 'monthly', 'bedtime', 'hourly', 'nightly',
    'prn', 'qhs', 'qam'
  ]);

  const foundMeds = [];
  const dosages = [];

  let match;
  while ((match = exactRegex.exec(text)) !== null) {
    foundMeds.push({
      name: match[1].toLowerCase(),
      start: match.index,
      end: match.index + match[0].length,
      hasDosage: false
    });
  }

  while ((match = dosageRegex.exec(text)) !== null) {
    dosages.push({ start: match.index, end: match.index + match[0].length });
  }

  const getTypoDistance = (a, b) => {
    if (Math.abs(a.length - b.length) > 2) return 99;
    const matrix = Array.from({ length: b.length + 1 }, (_, i) => [i]);
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  };

  // Records a single drug-name candidate: strips surrounding stop words / letter-free
  // tokens, then fuzzy-matches it to the known list (or marks it Unknown). Always dosed —
  // callers only invoke this for names anchored to a dosage.
  const recordCandidate = (rawName, baseStart) => {
    const isNoise = t => stopWords.has(t.toLowerCase()) || !/[a-zA-Z]/.test(t);
    let tokens = rawName.split(/\s+/).filter(Boolean);
    let removedLen = 0;
    while (tokens.length > 0 && isNoise(tokens[0])) {
      removedLen += tokens[0].length + 1;
      tokens.shift();
    }
    while (tokens.length > 0 && isNoise(tokens[tokens.length - 1])) {
      tokens.pop();
    }
    const name = tokens.join(' ').trim();
    if (!name) return;
    const nameStart = baseStart + removedLen;

    for (const rawToken of name.split(/\s+/).reverse()) {
      if (rawToken.length < 3) continue;
      const tokenLower = rawToken.toLowerCase();
      for (const known of sortedNames) {
        const knownFirstWord = known.toLowerCase().split(/\s+/)[0];
        const maxTypos = knownFirstWord.length < 7 ? 1 : 2;
        if (getTypoDistance(tokenLower, knownFirstWord) <= maxTypos) {
          const approxStart = nameStart + name.toLowerCase().indexOf(tokenLower);
          foundMeds.push({
            name: known.toLowerCase(),
            start: approxStart,
            end: approxStart + tokenLower.length,
            hasDosage: true
          });
          return;
        }
      }
    }

    if (name.toLowerCase().split(/\s+/).every(t => stopWords.has(t))) return;
    foundMeds.push({
      name: name + ' (Unknown)',
      start: nameStart,
      end: nameStart + name.length,
      hasDosage: true
    });
  };

  const sortedDoses = dosages.slice().sort((a, b) => a.start - b.start);
  for (let di = 0; di < sortedDoses.length; di++) {
    const dose = sortedDoses[di];
    const prevDose = di > 0 ? sortedDoses[di - 1] : null;
    if (prevDose && dose.start - prevDose.end <= 3) continue;

    const boundaries = [
      text.lastIndexOf('\n', dose.start - 1),
      text.lastIndexOf(',', dose.start - 1),
      text.lastIndexOf(';', dose.start - 1),
      prevDose ? prevDose.end : -1
    ];
    let phraseStart = Math.max(...boundaries) + 1;
    const phraseText = text.substring(phraseStart, dose.start);

    // Combination drugs are written "drugA/drugB" before a shared dose. Split only on
    // a "/" flanked by 3+ letters each side (a name boundary) — never digit slashes
    // from doses ("40mg/10mg"), vitals ("120/80"), or clinical shorthand ("N/V",
    // "A/E", "C/O") — and record each name as sharing the dose.
    if (/[a-zA-Z]{3}\/[a-zA-Z]{3}/.test(phraseText)) {
      let componentStart = phraseStart;
      for (const component of phraseText.split(/(?<=[a-zA-Z]{3})\/(?=[a-zA-Z]{3})/)) {
        recordCandidate(component, componentStart);
        componentStart += component.length + 1; // +1 skips the "/"
      }
      continue;
    }

    const exactMatchesInPhrase = foundMeds.filter(
      m => m.start >= phraseStart && m.end <= dose.start
    );
    if (exactMatchesInPhrase.length > 0) {
      const closestExact = exactMatchesInPhrase.sort((a, b) => b.end - a.end)[0];
      closestExact.hasDosage = true;
      // If there is meaningful text before the exact match (a longer/more-specific
      // name was typed), report the full phrase as Unknown so library gaps surface.
      const fullPhrase = text.substring(phraseStart, dose.start).trim();
      const matchedName = closestExact.name.toLowerCase();
      const fullPhraseLower = fullPhrase.toLowerCase().replace(/\s+/g, ' ');
      if (fullPhraseLower !== matchedName && fullPhraseLower.length > matchedName.length) {
        reportOnly.push(fullPhrase); // silent spreadsheet report, not shown to user
      }
      continue;
    }
    
    const rawPhrase = text.substring(phraseStart, dose.start);
    
    let matchPrefix = rawPhrase.match(/^[\s\d\.\-\*\u2022\u2192\u2190>]+/);
    let prefixLen = matchPrefix ? matchPrefix[0].length : 0;
    
    let candidateName = rawPhrase.substring(prefixLen).replace(/[:\-\s\u2192\u2190>]+$/, '');
    let candidateStart = phraseStart + prefixLen;

    if (candidateName.length > 80 || candidateName.includes('. ')) {
      const trailingMatch = rawPhrase.match(/([a-zA-Z]{3,}(?:\s+[a-zA-Z]{3,})?)\s*[:\-]?\s*$/);
      if (trailingMatch) {
        candidateName = trailingMatch[1].trim();
        candidateStart = dose.start - rawPhrase.length + rawPhrase.lastIndexOf(trailingMatch[1]);
      } else {
        continue;
      }
    }

    if (!candidateName) continue;
    recordCandidate(candidateName, candidateStart);
  }

  foundMeds.sort((a, b) => a.start - b.start);

  for (let i = 0; i < foundMeds.length; i++) {
    const med = foundMeds[i];
    if (med.hasDosage) continue;
    const nextMedStart = i + 1 < foundMeds.length ? foundMeds[i + 1].start : text.length;
    med.hasDosage = dosages.some(
      d => d.start >= med.end && d.start < med.end + 80 && d.start < nextMedStart
    );
  }

  const results = new Set();
  for (const med of foundMeds) {
    if (med.hasDosage) results.add(med.name);
  }
  return { medications: [...results], reportOnly };
}

export default {
  parseMedicationInput,
  addMedicationsFromInput,
  validateMedicationList,
  detectMultipleMedications,
  extractMedicationsFromText
};
